/*
 * File: Table.ts
 * File Created: 2023-12-16 03:41:46
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-20 10:44:42
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import {
    Component,
    Prop,
    Watch,
    MetaParser,
    IQuery,
    IQueryDefault,
    PropSync,
    ObjectHelper,
    IInputParamBase,
    IPaging,
    Emit,
    Retrier,
    IValServerInputConfig,
    Provide,
} from "@root/core";
import { IBaseComponentUITag, IServer } from "../../interfaces";
import { BaseTableCore, PRIVATE_ROW_ID } from "./lib/base-table-core";
import { BaseComponent } from "@root/components/base-classes";
import { debounce } from "@root/core/decorators/debounce";
import { PropInnateSync } from "@root/core/vue-props";

import TableHeader from "./lib/table-header.vue";
import TableBody from "./lib/table-body.vue";
import TablePagination from "./lib/table-pagination.vue";
import TablePopover from "./lib/table-popover.vue";
import {
    ITableBodyData,
    ITablePaginationData,
    ITablePaginationPortalData,
    ITablePopoverData,
    defaultBodyData,
    defaultPaginationData,
    defaultPopoverData,
} from "./lib/core";
import { ISearchHandle, uiSearch } from "@root/core/tags/extensions/ui-search";

const MIN_INTERVAL_VALUE = 1000;

const defaultPaging = {
    paging: {
        page: 1,
        pageSize: 10,
    },
};

export type ITableActionContext = {
    reload: (config?: any) => any;
};

@Component({
    components: {
        TableHeader,
        TableBody,
        TablePagination,
        TablePopover,
    },
})
export default class Table extends BaseComponent {
    /// public props ///////////////////////
    @Prop({ type: [String, Object], required: false })
    interface: string | MetaParser | undefined;

    /// The URI for this request.
    @Prop({ type: Object, required: false })
    server: IServer | undefined;
    innateParams: IServer["params"] = {};

    /// Other than server, pure data can be provided. they are mutually exclusive.
    @PropInnateSync("value", { type: Array, required: false })
    innateValue!: any[] | undefined;
    /// hint for PropInnateSync the value should be an array.
    private multiple = true;

    @Prop({ type: Object, required: false })
    uiTag?: IBaseComponentUITag;

    @Prop({ type: Object, required: false })
    declare data:
        | ITableBodyData
        | ITablePaginationData
        | ITablePaginationPortalData
        | ITablePopoverData;
    get mergedData() {
        return {
            ...defaultBodyData,
            ...defaultPaginationData,
            ...defaultPopoverData,
            ...this.data,
        };
    }

    @Prop({ type: Boolean, default: false })
    hidePagination!: boolean;

    @Prop({ type: Number, required: false })
    interval?: number;

    /// Events
    @Emit("selected")
    doSelectedUpdate(value) {
        return value;
    }
    /// after reload / search / sort
    @Emit("updated")
    doUpdatedUpdate(value) {
        return value;
    }

    /// public method
    @debounce(10)
    reload(config?: IValServerInputConfig) {
        if (this.server?.path != null)
            return this.reloadRequest(config)?.then(() => this.updateSelected());
        else {
            this.reloadValue();
            this.updateSelected();
        }
    }

    updateQueryData(updates: any[] | undefined | null) {
        if (!Array.isArray(updates) || updates.length === 0) return;
        const results = this.query?.data?.results;
        if (!Array.isArray(results) || results.length === 0) return;

        let touched = false;
        updates.forEach((update) => {
            const id = update?.id;
            if (!id) return;
            const idx = results.findIndex((row) => row?.id === id);
            if (idx < 0) return;
            const current = results[idx] ?? {};
            const next = { ...current, ...update };
            this.$set(results, idx, next);
            touched = true;
        });

        if (touched) this.updateSelected();
    }

    /// available slots /////////////////////
    publicSlots = {
        toolbox: "@toolbox",
        "popover-toolbox": "@popover-toolbox",
        "popover-toolbox-title": "@popover-toolbox-title",
    };

    /**
     * ✅ 提供給 table 深層元件使用
     * 讓任何 cell action 可以叫 table.reload()
     */
    @Provide("iv-table-action-context")
    tableActionContext: ITableActionContext = {
        reload: (config?: any) => this.reload(config),
    };

    /// life cycle hooks ////////////////////////////////
    // Val: no need to log the warning, since it could be delayed value from `iv-request`.
    // private mounted() {
    //     if (this.server === undefined && this.value === undefined) {
    //         console.warning(`<iv-table> Either "server" or "value" prop must be provided.`);
    //     }
    // }
    private mounted() {
        if (this.retrier != null) {
            this.retrier.start();
        }
    }
    private beforeDestroy() {
        if (this.retrier != null) {
            this.retrier.stop();
        }
    }

    /// private handlers ////////////////////////////////
    // hold the data / loading / error
    query: IQuery = { ...IQueryDefault(), loading: true };

    /// auto reload interval
    private retrier: Retrier | null = null;
    @Watch("interval", { immediate: true })
    onIntervalUpdated(value?: number) {
        /// clean the retrier
        if (this.retrier != null) {
            this.retrier.stop();
            this.retrier = null;
        }
        /// create retrier
        if (value != null) {
            this.retrier = new Retrier(
                () => {
                    this.reload({ silent: true });
                },
                Math.max(value, MIN_INTERVAL_VALUE),
            );
            this.retrier.start();
        }
    }

    // innate patterns
    @Watch("server.params", { immediate: true })
    onParamsUpdated(newValue: IServer["params"], oldValue: IServer["params"]) {
        if (ObjectHelper.equals(newValue, oldValue)) return;
        /// on params updated, update innate params with default paging
        const clone = ObjectHelper.deepClone(this.innateParams ?? {});
        if (oldValue != null) {
            removeKeysByShape(clone, oldValue);
        }
        this.innateParams = ObjectHelper.deepMerge(defaultPaging, clone, newValue);
    }
    @Watch("innateParams", { immediate: true })
    onInnateParamsUpdated(newValue: IServer["params"], oldValue: IServer["params"]) {
        /// if value equals, don't reload.
        if (ObjectHelper.equals(newValue, oldValue)) return;
        /// do the reload. the main trigger to do reload via param.
        this.$nextTick(this.reload);
    }

    @Watch("innateValue", { immediate: true })
    onValueUpdated(newValue: Table["innateValue"]) {
        /// undefined value no need to reload
        if (this.server?.path || newValue === undefined) return;

        /// validate value, must be keyed
        if (validateValue(newValue)) {
            this.$nextTick(this.reload);
        }
    }

    // server handler
    @Watch("server", { immediate: true })
    onServerUpdated(newValue: IServer | undefined, oldValue: IServer | undefined) {
        if (newValue == undefined || ObjectHelper.equals(newValue, oldValue)) return;
        /// query and result
        this.$nextTick(this.reload);
    }

    /// reload the static value
    reloadValue() {
        if (this.innateValue == null) {
            this.$set(this.query, "loading", false);
            return;
        }
        const params = this.innateParams;
        /// perform search
        const searchedValue = funcValueSearch(params?.where, this.innateValue);
        /// perform sort
        const sortedValue = funcValueSort(params?.orderBy, searchedValue);
        /// perform slice
        const { paging, results: pagedValue } = funcValuePaging(
            params?.paging,
            sortedValue,
        );

        this.$set(this.query, "loading", false);
        const data = {
            results: pagedValue,
            ...params,
            /// todo: paging will be moved into params
            paging,
        };
        this.$set(this.query, "data", data);
        this.$set(this.query, "error", undefined);
        this.doUpdatedUpdate(data);
    }

    /// reload the request
    reloadRequest(config?: IValServerInputConfig) {
        if (this.server?.path == null) return;
        const result = this.$server.useQuery(
            this.query,
            this.$server.R,
            this.server?.path as any,
            this.innateParams,
            config as any,
        );
        result.then((o) => {
            this.doUpdatedUpdate(o);
        });
        return result;
    }

    /// handle sort
    doUpdateSort(sort) {
        const result = funcUpdateSort(sort, this.innateParams, false);
        if (result == null) return; /// nothing to update
        /// patching the result
        const value = ObjectHelper.deepMerge(this.innateParams, result);
        this.innateParams = value;
    }

    /// handle paging
    doUpdatePaging(paging: Partial<IPaging> | null) {
        const result = funcUpdatePaging(paging, this.innateParams);
        /// patching the result
        const value = ObjectHelper.deepMerge(this.innateParams, result);
        this.innateParams = value;
    }

    /// handle searching
    @debounce(500)
    doUpdateSearching(searching: ISearchHandle | null) {
        const result = funcUpdateSearching(searching, this.innateParams);
        /// patching the result
        const value = ObjectHelper.deepMerge(this.innateParams, result);
        /// clean the search criteria
        if (searching == null) delete value.where?.OR;
        this.innateParams = value;
    }

    /// handle select
    // sync back the selected row
    selected: Record<string, any> | null = null;
    @Watch("selected") doSelectedUpdated(value) {
        /// emit the selected changes
        this.doSelectedUpdate(value);
    }
    // while reload, refresh the selected changes.
    updateSelected() {
        const selected = this.selected;
        if (selected == null) return;
        const found = (
            (this.server?.path ? this.query.data?.results : this.innateValue) ?? []
        ).find((value) => {
            return (
                (value.id != null && value.id === selected.id) ||
                (value[PRIVATE_ROW_ID] != null &&
                    value[PRIVATE_ROW_ID] === selected[PRIVATE_ROW_ID])
            );
        });
        /// if the selected is gone, also remove the selected.
        if (ObjectHelper.equals(selected, found) == false) this.selected = found;
    }
    get currentSelectedId() {
        if (this.selected == null) return null;
        const rtn = `hover-${this._uid}-${BaseTableCore.getDataId(this.selected)}`;
        return rtn;
    }

    // interface parser
    get parsedInterface(): MetaParser {
        return parseInterface(
            this.interface == "" ? undefined : this.interface,
            this.query,
        );
    }
}

/// private helpers
// parse string interface into MetaResult[]
function parseInterface(
    inf: string | MetaParser | undefined,
    query: IQuery | undefined,
): MetaParser {
    /// bypassing MetaParser
    if (inf instanceof MetaParser) return inf;

    if (inf != null) {
        /// parse interface into MetaParser
        return new MetaParser(inf);
    }
    /// fallback to default
    if ((query?.data?.results?.length ?? 0) === 0) return new MetaParser();

    /// generate default meta
    let ref = query?.data?.results[0];
    return new MetaParser(Object.keys(ref));
}

// update innateParams with new paging rule.
function funcUpdatePaging(
    paging: Partial<IPaging> | null,
    innateParams: Table["innateParams"],
) {
    /// clone the params
    const cloneParams: Table["innateParams"] =
        innateParams == null ? {} : ObjectHelper.deepClone(innateParams);

    /// if null, just delete the paging
    if (paging == null) {
        delete cloneParams.paging;
    } else {
        /// do update
        cloneParams.paging = paging;
    }

    return cloneParams;
}

// update innateParams with new searching rule
function funcUpdateSearching(
    searching: ISearchHandle | null,
    innateParams: Table["innateParams"],
) {
    /// clone the params
    let cloneParams: Table["innateParams"] =
        innateParams == null ? {} : ObjectHelper.deepClone(innateParams);

    /// if null, clean the search
    if (searching == null) {
        delete cloneParams.where;
    } else {
        cloneParams = Object.assign(cloneParams, searching);
    }

    return cloneParams;
}

// update innateParams with new sort rule.
function funcUpdateSort(
    sort,
    innateParams: Table["innateParams"],
    multiSort: boolean = true,
) {
    const name = Object.keys(sort)[0];
    const value = sort[name];

    /// clone the params
    const cloneParams: Table["innateParams"] =
        innateParams == null ? {} : ObjectHelper.deepClone(innateParams);

    /// find the index to replace
    let orderBy = cloneParams.orderBy ?? [];

    if (multiSort === true) {
        /// multiple sort
        const idx = orderBy.findIndex((item) => {
            if (item[name] != null) return true;
            return false;
        });

        /// nothing to update
        if (idx < 0 && value == null) return null;

        /// add
        if (idx < 0) orderBy.push({ [name]: value });
        /// removal
        else if (value == null) orderBy.splice(idx, 1);
        /// modify
        else orderBy[idx][name] = value;
    } else {
        /// single sort
        orderBy = value == null ? [] : [{ [name]: value }];
    }

    /// do update
    cloneParams.orderBy = orderBy;
    return cloneParams;
}

// slice value with paging rule.
function funcValuePaging(
    paging: IInputParamBase["paging"],
    value: any[],
): { paging: IPaging; results: any[] } {
    /// return default
    if (paging == null)
        return {
            paging: {
                ...defaultPaging.paging,
                total: value.length,
                totalPages: Math.ceil(value.length / defaultPaging.paging.pageSize),
            },
            results: value,
        };

    // Set default values if page or pageSize are not provided
    const page = paging.page ?? 1; // Default to page 1 if not provided
    const pageSize = paging.pageSize ?? value.length; // Default to the entire array length if pageSize not provided

    // Calculate the slice start index
    const startIndex = (page - 1) * pageSize;
    // Calculate the slice end index
    const endIndex = startIndex + pageSize;

    // Return the sliced array without affecting the original
    return {
        paging: {
            ...defaultPaging.paging,
            page,
            pageSize,
            total: value.length,
            totalPages: Math.ceil(value.length / pageSize),
        },
        results: value.slice(startIndex, endIndex),
    };
}

// search value with searching rule
function funcValueSearch(where: ISearchHandle["where"], value: any[]): any[] {
    /// todo: only handle first level "OR" for now.
    const rules = where?.OR;
    if (rules == null) return value;

    return value.filter((item) => {
        /// go through all the rules
        let pass = false;
        for (let rule of rules) {
            const name = Object.keys(rule)[0];
            const currentValue = item[name];
            const ruleObject = rule[name];

            pass = uiSearch.match(currentValue, ruleObject);
            if (pass == true) break;
        }
        return pass;
    });
}

// sort value data with sorting rule.
function funcValueSort(orderBy: IInputParamBase["orderBy"], value: any[]): any[] {
    if (!orderBy || orderBy.length === 0) {
        return value; // No sorting rule provided, return the original array
    }

    return value.slice().sort((a, b) => {
        for (const rule of orderBy) {
            const key = Object.keys(rule)[0];
            const direction = rule[key];

            const lft = a[key] ?? null;
            const rgt = b[key] ?? null;
            if (lft < rgt) {
                return direction === "asc" ? -1 : 1;
            }
            if (lft > rgt) {
                return direction === "asc" ? 1 : -1;
            }
            // If the current keys are equal, move to the next sort rule
        }

        return 0; // If all keys are equal, keep original order
    });
}

/// validate value: must be keyed
function validateValue(value: any[] | undefined): boolean {
    /// no value
    if (value == undefined) return true;

    if (!Array.isArray(value)) {
        console.error(
            `<iv-table> prop "value" must be an array. (${JSON.stringify(value)})`,
        );
        return false;
    }

    /// check all ids, auto apply if undefined
    let autoId = value.reduce((final, o) => {
        return Math.max(final, o[PRIVATE_ROW_ID] ?? 0);
    }, 0);
    for (let o of value) {
        if (o[PRIVATE_ROW_ID] == undefined) {
            o[PRIVATE_ROW_ID] = ++autoId;
            // console.error(`<iv-table> prop "value" must be keyed with "id".`);
            // return false;
        }
    }
    if (autoId !== 0)
        console.debug(
            `<iv-table> prop "value" being auto-keyed with "${PRIVATE_ROW_ID}".`,
        );

    return true;
}

function removeKeysByShape(target: Record<string, any>, shape: any) {
    if (!ObjectHelper.isPlainObject(shape) || !ObjectHelper.isPlainObject(target)) return;
    for (const key of Object.keys(shape)) {
        const nextShape = shape[key];
        const nextTarget = target[key];
        if (
            ObjectHelper.isPlainObject(nextShape) &&
            ObjectHelper.isPlainObject(nextTarget)
        ) {
            removeKeysByShape(nextTarget, nextShape);
            if (Object.keys(nextTarget).length === 0) delete target[key];
        } else {
            delete target[key];
        }
    }
}
